import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, Send, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/context/AuthContext";

/* FriendChatDock — global collapsible chat between friends.
   Bottom-right of the viewport, closed by default. When opened, shows the
   friends list; picking a friend opens their conversation. Polls unread every
   6s (closed) and messages every 3s (open on a conversation). Text-only —
   built for arranging practice, not full messaging. */

const UNREAD_POLL_MS = 6000;
const MESSAGE_POLL_MS = 3000;
const MSG_MAX_LEN = 1000;

const AvatarBubble = ({ picture, name, size = 34 }) => {
  const initial = (name || "?").charAt(0).toUpperCase();
  if (picture) {
    return (
      <img
        src={picture}
        alt={name}
        className="shrink-0 rounded-full object-cover"
        style={{ height: size, width: size }}
      />
    );
  }
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full bg-white/5 text-xs font-bold text-zinc-200"
      style={{ height: size, width: size }}
    >
      {initial}
    </span>
  );
};

const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

export const FriendChatDock = () => {
  const [open, setOpen] = useState(false);
  const [friends, setFriends] = useState([]);
  const [activeFriend, setActiveFriend] = useState(null); // {user_id, name, picture}
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState({ total: 0, by_friend: {} });
  const scrollRef = useRef(null);

  // Load friends once when opened.
  const loadFriends = useCallback(async () => {
    try {
      const r = await api.get("/friends");
      setFriends(r.data?.friends || []);
    } catch {
      /* ignore */
    }
  }, []);

  // Listen for cross-page requests to open a specific conversation
  // (dispatched from Friends.jsx's "Chat" button). Payload: {friend}
  // where friend has at least user_id + name + optional picture / username.
  useEffect(() => {
    const onOpenWith = (e) => {
      const f = e?.detail?.friend;
      if (!f?.user_id) return;
      setOpen(true);
      setActiveFriend({
        user_id: f.user_id,
        name: f.name,
        username: f.username,
        picture: f.picture,
      });
      setMessages([]);
      setDraft("");
    };
    window.addEventListener("salespit:open-chat-with", onOpenWith);
    return () => window.removeEventListener("salespit:open-chat-with", onOpenWith);
  }, []);

  // Unread poll — always on, even when the dock is closed.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get("/friends/messages/unread");
        if (alive) setUnread(r.data || { total: 0, by_friend: {} });
      } catch {
        /* ignore */
      }
    };
    tick();
    const iv = setInterval(tick, UNREAD_POLL_MS);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, []);

  // When opened, load the friends list.
  useEffect(() => {
    if (open && friends.length === 0) loadFriends();
  }, [open, friends.length, loadFriends]);

  // When a friend is selected, poll their conversation.
  useEffect(() => {
    if (!activeFriend) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get(`/friends/${activeFriend.user_id}/messages`);
        if (!alive) return;
        setMessages(r.data?.messages || []);
        // Mark read (best-effort) and refresh unread summary.
        try {
          await api.post(`/friends/${activeFriend.user_id}/messages/read`);
          const u = await api.get("/friends/messages/unread");
          if (alive) setUnread(u.data || { total: 0, by_friend: {} });
        } catch { /* ignore */ }
      } catch {
        /* ignore */
      }
    };
    tick();
    const iv = setInterval(tick, MESSAGE_POLL_MS);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [activeFriend]);

  // Auto-scroll to newest on message change.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, activeFriend]);

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || sending || !activeFriend) return;
    setSending(true);
    try {
      const r = await api.post(`/friends/${activeFriend.user_id}/messages`, { text });
      setMessages((prev) => [...prev, r.data]);
      setDraft("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message.");
    } finally {
      setSending(false);
    }
  };

  const openConversation = (friend) => {
    setActiveFriend({
      user_id: friend.user_id,
      name: friend.name,
      username: friend.username,
      picture: friend.picture,
    });
    setMessages([]);
    setDraft("");
  };

  const totalUnread = unread.total || 0;

  return (
    <div
      className="pointer-events-none fixed bottom-5 right-5 z-40 flex flex-col items-end gap-3"
      data-testid="friend-chat-dock"
    >
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.18 }}
            data-testid="friend-chat-panel"
            className="pointer-events-auto flex h-[75vh] max-h-[560px] w-[340px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#141414]/95 shadow-2xl backdrop-blur-xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              {activeFriend ? (
                <button
                  onClick={() => setActiveFriend(null)}
                  data-testid="chat-back-btn"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-white"
                >
                  <ArrowLeft size={15} className="text-zinc-400" />
                  {activeFriend.name}
                </button>
              ) : (
                <span className="inline-flex items-center gap-2 text-sm font-bold text-white">
                  <MessageSquare size={15} className="text-[#E52525]" />
                  Friend chat
                </span>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="chat-dock-close-btn"
                className="flex h-7 w-7 items-center justify-center rounded-full text-zinc-400 hover:bg-white/5 hover:text-white"
              >
                <X size={14} />
              </button>
            </div>

            {/* Body */}
            {activeFriend ? (
              <>
                <div
                  ref={scrollRef}
                  data-testid="chat-thread"
                  className="flex-1 space-y-2 overflow-y-auto px-4 py-3"
                >
                  {messages.length === 0 ? (
                    <p className="pt-6 text-center text-xs text-zinc-500">
                      Say hi — start with a practice session invite.
                    </p>
                  ) : (
                    messages.map((m) => (
                      <div
                        key={m.id}
                        data-testid={`chat-msg-${m.from_self ? "self" : "peer"}`}
                        className={`flex flex-col ${m.from_self ? "items-end" : "items-start"}`}
                      >
                        <span
                          className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-[13px] leading-snug ${
                            m.from_self
                              ? "bg-[#E52525] text-white"
                              : "bg-white/8 text-zinc-100"
                          }`}
                        >
                          {m.text}
                        </span>
                        <span className="mt-0.5 text-[10px] text-zinc-600">
                          {formatTime(m.created_at)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
                <form
                  onSubmit={sendMessage}
                  className="flex items-center gap-2 border-t border-white/10 px-3 py-3"
                >
                  <input
                    data-testid="chat-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Message"
                    maxLength={MSG_MAX_LEN}
                    className="flex-1 rounded-full border border-white/10 bg-black/40 px-3.5 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-[#E52525]/50 focus:outline-none"
                  />
                  <button
                    type="submit"
                    data-testid="chat-send-btn"
                    disabled={!draft.trim() || sending}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E52525] text-white transition-colors hover:bg-[#c81f1f] disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Send"
                  >
                    <Send size={15} />
                  </button>
                </form>
              </>
            ) : (
              <div className="flex-1 overflow-y-auto" data-testid="chat-friends-list">
                {friends.length === 0 ? (
                  <p className="p-6 text-center text-xs text-zinc-500">
                    Add some friends first — they'll appear here to chat with.
                  </p>
                ) : (
                  friends.map((f) => {
                    const unreadFor = unread.by_friend?.[f.user_id] || 0;
                    return (
                      <button
                        key={f.friendship_id}
                        onClick={() => openConversation(f)}
                        data-testid={`chat-friend-${f.friendship_id}`}
                        className="flex w-full items-center gap-3 border-b border-white/5 px-4 py-3 text-left transition-colors hover:bg-white/5"
                      >
                        <AvatarBubble picture={f.picture} name={f.name} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-white">
                            {f.name}
                          </p>
                          {f.username && (
                            <p className="truncate text-xs text-zinc-500">
                              {f.username}
                            </p>
                          )}
                        </div>
                        {unreadFor > 0 && (
                          <span
                            data-testid={`chat-unread-${f.user_id}`}
                            className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#E52525] px-1.5 text-[10px] font-bold text-white"
                          >
                            {unreadFor > 9 ? "9+" : unreadFor}
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="friend-chat-toggle-btn"
        aria-label="Friend chat"
        className="pointer-events-auto relative flex h-12 w-12 items-center justify-center rounded-full bg-[#E52525] text-white shadow-2xl transition-colors hover:bg-[#c81f1f]"
      >
        <MessageSquare size={20} />
        {!open && totalUnread > 0 && (
          <span
            data-testid="friend-chat-unread-badge"
            className="pointer-events-none absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white px-1.5 text-[10px] font-bold text-[#E52525] ring-2 ring-[#0A0A0A]"
          >
            {totalUnread > 9 ? "9+" : totalUnread}
          </span>
        )}
      </button>
    </div>
  );
};
